import { useEffect, useState } from 'react';

// Detects when a newer build has been deployed while this tab stayed open.
// SPAs keep running their original JS until a full reload, so without this an
// open tab silently runs stale code after a deploy (the 2026-06-11 incident).
// Polls /version.json (the DEPLOYED build id) on focus + interval and compares
// to this tab's compiled-in build id. Read-only; never reloads on its own.
//
// See specs/POST-DEPLOY-SAFETY.md → Build-freshness gate.

const POLL_MS = 5 * 60 * 1000;

export function useVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let active = true;

    const check = async () => {
      try {
        // Cache-bust + no-store so we always see the freshly-deployed id.
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const { build } = (await res.json()) as { build?: string };
        if (active && build && build !== __APP_BUILD__) setUpdateAvailable(true);
      } catch {
        /* offline / transient — try again next tick */
      }
    };

    void check();
    const id = window.setInterval(check, POLL_MS);
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return updateAvailable;
}
